// src/lib/schema/components/ProductSchema.tsx

import React from 'react';
import { generateProductSchema, generateBreadcrumbSchema } from '@/lib/schema';
import { shopInfoContainer } from '@/containers/shopInfo.container';
import { ProductData, Product, BreadcrumbList, Graph } from '../types';

/**
 * Props for the ProductSchema component
 */
interface ProductSchemaProps {
  product: ProductData;
  lang: string;
  includeBreadcrumbs?: boolean;
}

/**
 * Component that renders the JSON-LD schema for a product page
 */
export function ProductSchema({ 
  product, 
  lang, 
  includeBreadcrumbs = false 
}: ProductSchemaProps): React.ReactElement | null {
  if (!product) return null;

  // Create the full URL for the product
  const { baseURL } = shopInfoContainer;
  const productURL = product.titles?.url
    ? `${baseURL}/${lang}/shop/carpets/${product.titles.url}`
    : product.titles.url  // TODO: Remove backwards compatibility with product.titles.url after merge to master
      ? `${baseURL}/${lang}/shop/carpets/${product.titles.url}`
      : `${baseURL}/${lang}/shop/carpets/${product.productID}`;

  // Create schemas array
  const schemas: (Product | BreadcrumbList)[] = [];

  // Add product schema
  const productSchema = generateProductSchema(product, lang, productURL);
  if (productSchema) {
    schemas.push(productSchema);
  }

  // Add breadcrumb schema if requested
  if (includeBreadcrumbs) {
    const unitSystem = lang === 'en' ? 'USC' : 'SI';
    const productTitle = product?.titles?.[unitSystem]?.checkout ||
                        product?.titles?.[unitSystem]?.standard ||
                        product?.title ||
                        product?.productID;

    const breadcrumbItems = [
      { name: 'Home', url: `${baseURL}/${lang}` },
      { name: 'Shop', url: `${baseURL}/${lang}/shop` },
      { name: 'Carpets', url: `${baseURL}/${lang}/shop/carpets` }
    ];

    // Add a style category if available
    if (product.style) {
      breadcrumbItems.push({
        name: product.style,
        url: `${baseURL}/${lang}/shop/carpets?style=${encodeURIComponent(product.style)}`
      });
    }

    // Add product as final item
    breadcrumbItems.push({ name: productTitle as string, url: productURL });

    const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);
    if (breadcrumbSchema) {
      schemas.push(breadcrumbSchema);
    }
  }

  // Create combined schema or return single schema
  const schemaObj = schemas.length > 1
    ? { '@context': 'https://schema.org', '@graph': schemas } as Graph
    : schemas[0] || null;

  // Return null if no schemas
  if (!schemaObj) return null;

  // Convert to JSON string
  const jsonLdString = JSON.stringify(schemaObj);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}